import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useParams } from "@tanstack/react-router";
import { Search, X } from "lucide-react";

import {
  CosmosQueryFileSchema,
  CosmosQueryLookupSchema,
  CosmosQueryResultItemSchema,
  CosmosSourceSchema,
  QuickLookupSchema,
} from "@api/generated/types.gen";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import DocumentPreviewDialog from "@components/DocumentPreviewDialog";
import { OcrHighlightRequest } from "@components/FilePreview/types";
import { Input } from "@components/ui/input";

import { useReindexFiles } from "@hooks/mutations/use-files";
import { useFiles } from "@hooks/queries/use-files";
import { useCosmosQuery, useQuickLookups } from "@hooks/queries/use.cosmos";

import { isQuickLookupsEnabled } from "../../../../constants/quick-lookups";
import {
  CosmosQueryResponseWithReadiness,
  FileWithIndexing,
  isFileSemanticSearchReady,
} from "../../../../types/file-indexing";
import { useRecentSearches } from "../hooks/useRecentSearches";
import { AiAnswerUnavailable } from "./AiAnswerUnavailable";
import { AssessedConditionField } from "./AssessedConditionField";
import { QuickLookups } from "./QuickLookups";
import { QuickSearches } from "./QuickSearches";
import { RecentSearches } from "./RecentSearches";
import { SearchAnswer } from "./SearchAnswer";

type SearchResult = CosmosQueryResultItemSchema;
type FileData = CosmosQueryFileSchema;

interface SelectedDocument {
  file: FileData;
  page: number;
  /**
   * The clicked cited passage to highlight in the preview (MDRS-134). Kept as
   * an array so the preview can draw multiple boxes for one multi-line passage.
   */
  highlights?: OcrHighlightRequest[];
}

export const SemanticSearchInput = () => {
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };

  const [query, setQuery] = useState("");
  // The Quick Lookup being answered, if any. A lookup has no free text, so it
  // is held apart from `query`: typing clears it and goes to Document Search.
  const [activeLookup, setActiveLookup] = useState<QuickLookupSchema | null>(null);
  // The lookup the service says it answered. Its `question` is what's shown,
  // so the wording always matches what was actually asked.
  const [answeredLookup, setAnsweredLookup] =
    useState<CosmosQueryLookupSchema | null>(null);
  // The expert-entered condition being assessed, sent with every Quick Lookup.
  // Clinical content, so it lives in memory only — never browser storage.
  const [assessedCondition, setAssessedCondition] = useState("");
  const [answer, setAnswer] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [sources, setSources] = useState<CosmosSourceSchema[]>([]);
  const [notReady, setNotReady] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<SelectedDocument | null>(
    null
  );
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const quickLookupsEnabled = isQuickLookupsEnabled();
  const { data: quickLookupRegister } = useQuickLookups(quickLookupsEnabled);

  const cosmosQuery = useCosmosQuery();
  const { mutateAsync: queryCosmosAsync } = cosmosQuery;
  const reindexFiles = useReindexFiles();
  const { mutateAsync: reindexFilesAsync } = reindexFiles;

  const { data: files } = useFiles({ case_number: caseNumber });
  const { items: recentItems, add: addRecentSearch } = useRecentSearches(caseNumber);

  const { totalCount, indexedCount, indexingCount, failedFiles } = useMemo(() => {
    const list = (files ?? []) as FileWithIndexing[];
    const indexed = list.filter(isFileSemanticSearchReady).length;
    const failed = list.filter((f) => f.indexing_status === "FAILED");
    // Files still pending/in-progress count as "indexing"; failed ones are
    // called out separately since they need a user-triggered retry.
    const indexing = list.length - indexed - failed.length;
    return {
      totalCount: list.length,
      indexedCount: indexed,
      indexingCount: indexing,
      failedFiles: failed,
    };
  }, [files]);

  const hasFailedFiles = failedFiles.length > 0;
  // "Still being indexed" only applies while files are genuinely PENDING /
  // IN_PROGRESS. A file at FAILED is done trying — it won't clear on its own, so
  // it must not be counted as "not indexed yet" or the amber notice masks the
  // Re-index card (which is the actual recovery path).
  const noFilesIndexedYet =
    totalCount > 0 && indexedCount === 0 && indexingCount > 0;

  // Runs a Document Search for `query`, or the given Quick Lookup's fixed
  // question. The service 400s on a request with neither, so an empty query
  // never leaves here. `condition` is passed in rather than read from state so
  // applying a new one can re-run the lookup in the same tick.
  const runQuery = useCallback(
    async (lookup: QuickLookupSchema | null, condition = "") => {
      if (!caseNumber) return;
      if (!lookup && !query.trim()) return;

      // Local short-circuit: don't fire a request when nothing is indexed yet.
      if (noFilesIndexedYet) {
        setIsPopoverOpen(true);
        setAnswer("");
        setResults([]);
        setNotReady(true);
        setHasSearched(false);
        setHasError(false);
        return;
      }

      setAnswer("");
      setResults([]);
      setAnsweredLookup(null);
      setNotReady(false);
      setHasSearched(false);
      setHasError(false);
      setIsPopoverOpen(true);

      try {
        const response = (await queryCosmosAsync(
          lookup
            ? {
                case_number: caseNumber,
                lookup_id: lookup.id,
                ...(condition ? { assessment_conditions: [condition] } : {}),
              }
            : { case_number: caseNumber, query: query }
        )) as CosmosQueryResponseWithReadiness | undefined;

        // Backend reports not_ready when no files are indexed. If that's only
        // because the case's files FAILED (not because indexing is still running),
        // fall through to the completed-search path so the Re-index card renders
        // instead of the "still being indexed" notice, which would never clear.
        if (response?.not_ready && !hasFailedFiles) {
          setNotReady(true);
          setAnswer("");
          setResults([]);
          setSources([]);
        } else {
          // A settled, non-not-ready response — including a falsy/empty one —
          // counts as a completed search so the no-match state can render
          // instead of a blank popover.
          const items = response?.results || [];
          setNotReady(false);
          setAnswer(response?.answer ?? "");
          setResults(items);
          setSources(response?.sources ?? []);
          setAnsweredLookup(response?.lookup ?? null);
          setHasSearched(true);
          // Only record a recent search when the query genuinely ran against
          // indexed content. A not_ready response (reached here only because files
          // FAILED) never completed, so recording it would persist a misleading
          // "0 docs" entry for a search the user didn't actually get an answer to.
          // Recent searches are Document Search history; a Quick Lookup is
          // one click away in its own list.
          if (!response?.not_ready && !lookup) {
            addRecentSearch(query, items.length);
          }
        }
      } catch (error) {
        console.error("Error performing semantic search:", error);
        setHasError(true);
        setAnswer("");
        setResults([]);
        setNotReady(false);
      }
    },
    [
      query,
      caseNumber,
      queryCosmosAsync,
      noFilesIndexedYet,
      hasFailedFiles,
      addRecentSearch,
    ]
  );

  const onSearch = useCallback(() => runQuery(null), [runQuery]);

  const onSelectLookup = (lookup: QuickLookupSchema) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setQuery("");
    setActiveLookup(lookup);
    void runQuery(lookup, assessedCondition);
  };

  const onApplyCondition = (condition: string) => {
    setAssessedCondition(condition);
    if (activeLookup) void runQuery(activeLookup, condition);
  };

  const onQueryChange = (value: string) => {
    // Typing is Document Search; it leaves any Quick Lookup.
    if (activeLookup) setActiveLookup(null);
    setQuery(value);
  };

  // Re-index every failed file, then re-run the current search so the answer
  // can pick up the newly-indexed evidence. Kept keeping the popover open so the
  // spinner + subsequent result render in place.
  const onReindexAndAnswer = useCallback(async () => {
    if (failedFiles.length === 0) return;
    try {
      await reindexFilesAsync(failedFiles.map((f) => f.id));
      // Files won't be INDEXED instantly, but the useFiles poll picks up the
      // status change; re-running the search surfaces whatever is ready now.
      await runQuery(activeLookup, assessedCondition);
    } catch (error) {
      console.error("Error re-indexing failed files:", error);
      setHasError(true);
    }
  }, [failedFiles, reindexFilesAsync, runQuery, activeLookup, assessedCondition]);

  // Auto-search with debounce when user stops typing
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    // A Quick Lookup runs once on selection; there's nothing to debounce.
    if (activeLookup) return;

    if (!query.trim()) {
      setIsPopoverOpen(false);
      setAnswer("");
      setResults([]);
      setHasSearched(false);
      setHasError(false);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      onSearch();
    }, 1000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [query, onSearch, activeLookup]);

  // Close results on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsPopoverOpen(false);
      }
    };

    if (isPopoverOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isPopoverOpen]);

  // The preview dialog resolves the file-scoped SAS URL itself from the id, so
  // opening a cited source is just recording which file + page to show.
  const onOpenPDF = (
    file: FileData,
    page: number,
    snippet?: string,
    bboxes?: CosmosSourceSchema["bboxes"]
  ) => {
    // Highlight only the passage the user clicked (MDRS-134). Fanning out to
    // every cited source in the document lit up unrelated text — other sources
    // share a page or a few incidental words with the clicked one. The clicked
    // source highlights via exact backend `bboxes` or, for older responses, a
    // `snippet` matched against OCR.
    const hasHighlight =
      (snippet && snippet.length > 0) || (bboxes && bboxes.length > 0);

    setSelectedDocument({
      file,
      page,
      highlights: hasHighlight
        ? [{ page, snippet: snippet ?? "", bboxes }]
        : undefined,
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    } else if (event.key === "Escape") {
      setIsPopoverOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    if ((query.trim() || activeLookup) && (answer || results.length > 0)) {
      setIsPopoverOpen(true);
    }
  };

  // Blur is tracked on the container, not the input, so tabbing from the input
  // onto a dropdown button keeps the dropdown mounted for keyboard users.
  const handleContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (containerRef.current?.contains(e.relatedTarget as Node | null)) return;
    setIsFocused(false);
  };

  // Picking from the dropdown unmounts it, and a removed button doesn't
  // reliably fire blur — so hand focus back to the input after a keyboard pick.
  // A no-op for mouse picks, which never move focus off the input.
  const onPickLookup = (lookup: QuickLookupSchema) => {
    onSelectLookup(lookup);
    inputRef.current?.focus();
  };

  const onPickSearch = (value: string) => {
    setQuery(value);
    inputRef.current?.focus();
  };

  const isLoading = cosmosQuery.isPending;
  const hasActiveSearch = query.trim().length > 0 || activeLookup !== null;

  // When files failed to index we surface the dedicated "AI Answer unavailable"
  // card (with a re-index CTA) instead of the plain no-matches empty state,
  // even if this particular query returned nothing.
  const showUnavailable =
    hasFailedFiles && hasSearched && !isLoading && !notReady && !hasError;

  const noMatches =
    hasSearched &&
    !isLoading &&
    !notReady &&
    !showUnavailable &&
    !answer &&
    results.length === 0;

  return (
    <div
      ref={containerRef}
      className="semantic-search-container relative"
      onBlur={handleContainerBlur}
    >
      {/* p-1 reserves room on all sides for the focus ring so it isn't clipped
          by the flex parent / row edge above. */}
      <div className="relative p-1">
        <Search className="pointer-events-none absolute left-5 top-1/2 size-4 -translate-y-1/2 text-foreground-muted" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          placeholder="Type to search case documents..."
          className="h-10 min-h-10 w-full rounded-full !bg-background-white px-10 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        />
        <div className="absolute right-5 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {(query || activeLookup) && !isLoading && (
            <button
              aria-label="Clear search"
              onMouseDown={(e) => {
                e.preventDefault();
                setActiveLookup(null);
                setQuery("");
                inputRef.current?.focus();
              }}
              className="text-foreground-muted hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
          {isLoading && <LoadingIcon className="size-4 animate-spin-ccw" />}
        </div>
      </div>

      {/* Quick + recent searches dropdown — shown on focus when query is empty */}
      {isFocused && !hasActiveSearch && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 w-full overflow-hidden rounded-xl border bg-background-white py-3 shadow-[0px_8px_24px_0px_rgba(0,0,0,0.12)]">
          {quickLookupsEnabled && (
            <div className="border-b px-4 pb-4">
              <QuickLookups onSelect={onPickLookup} />
            </div>
          )}
          <div className={`px-4 pb-4 ${recentItems.length > 0 ? "border-b" : ""}`}>
            <QuickSearches onSelect={onPickSearch} />
          </div>
          <RecentSearches items={recentItems} onSelect={onPickSearch} />
        </div>
      )}

      {/* Results popover */}
      {isPopoverOpen && hasActiveSearch && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[560px] w-full overflow-y-auto rounded-xl border bg-background-white shadow-[0px_8px_24px_0px_rgba(0,0,0,0.12)]">
          {notReady ? (
            <div className="m-4 rounded-md border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700">
              <p className="font-medium">Getting your documents ready</p>
              <p className="mt-1">
                AI search will be ready once we finish preparing your documents. This
                usually takes 1–2 minutes per document.
              </p>
            </div>
          ) : hasError ? (
            <div className="m-4 rounded-md border border-destructive-200 bg-destructive-50 p-4 text-sm text-destructive-700">
              <p className="font-medium">Search failed</p>
              <p className="mt-1">
                Something went wrong while searching. Please try again.
              </p>
            </div>
          ) : showUnavailable ? (
            <AiAnswerUnavailable
              failedFiles={failedFiles}
              totalCount={totalCount}
              isReindexing={reindexFiles.isPending}
              onReindex={onReindexAndAnswer}
            />
          ) : noMatches && activeLookup ? (
            <div className="px-4 py-6">
              <LookupHeader
                label={activeLookup.label}
                question={answeredLookup?.question ?? activeLookup.fixed_question}
                boundary={quickLookupRegister?.boundary}
              />
              <AssessedConditionField
                value={assessedCondition}
                onApply={onApplyCondition}
                disabled={isLoading}
              />
              <p className="mt-4 text-sm font-medium text-foreground-muted">
                Nothing in this case&rsquo;s documents answers this Quick Lookup.
              </p>
            </div>
          ) : noMatches ? (
            <div className="flex flex-col items-center gap-4 px-4 py-8 text-center">
              <div className="flex h-14 items-center justify-center rounded-full bg-background px-[18px]">
                <Search className="size-5 text-foreground-muted" />
              </div>
              <div className="flex flex-col items-center gap-3">
                <h3 className="text-xl font-medium text-foreground">
                  No matches found for your search term &ldquo;{query}&rdquo;
                </h3>
                <p className="text-sm font-medium text-foreground-muted">
                  Try broader terms, or check spelling.
                </p>
              </div>
              <div className="flex flex-col items-center pt-4">
                <QuickSearches
                  onSelect={setQuery}
                  title="Try these instead"
                  centered
                />
              </div>
            </div>
          ) : (
            <>
              <div className="border-b px-4 pb-4 pt-3">
                {activeLookup ? (
                  <>
                    <LookupHeader
                      label={activeLookup.label}
                      question={
                        answeredLookup?.question ?? activeLookup.fixed_question
                      }
                      boundary={quickLookupRegister?.boundary}
                    />
                    <AssessedConditionField
                      value={assessedCondition}
                      onApply={onApplyCondition}
                      disabled={isLoading}
                    />
                  </>
                ) : (
                  <QuickSearches onSelect={setQuery} title="Suggested Searches" />
                )}
              </div>

              {indexingCount > 0 && (
                <div
                  className="mx-4 mt-4 rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700"
                  data-testid="partial-indexing-banner"
                >
                  Showing results from {indexedCount} of {totalCount} documents.
                  {indexingCount} still getting ready&hellip;
                </div>
              )}

              {(answer || isLoading) && (
                <SearchAnswer
                  answer={isLoading ? "" : answer}
                  isLoading={isLoading}
                  results={isLoading ? [] : results}
                  sources={isLoading ? [] : sources}
                  onOpenSource={(file, page, snippet, bboxes) => {
                    onOpenPDF(file, page, snippet, bboxes);
                    setIsPopoverOpen(false);
                  }}
                />
              )}
            </>
          )}
        </div>
      )}

      {selectedDocument && (
        <DocumentPreviewDialog
          onClose={() => {
            setSelectedDocument(null);
            // Opening a cited source closes the results popover (line above);
            // reopen it so the user lands back on their search instead of a
            // blank input. Only when the query is still the one that produced
            // these results — clearing it while the preview was open already
            // reset everything via the debounce effect.
            if (query.trim() || activeLookup) setIsPopoverOpen(true);
          }}
          file={selectedDocument.file}
          page={selectedDocument.page}
          caseNumber={caseNumber}
          highlights={selectedDocument.highlights}
        />
      )}
    </div>
  );
};

// The question a Quick Lookup answered, and the pack's boundary line. The
// question is the service's wording, never a frontend copy.
const LookupHeader = ({
  label,
  question,
  boundary,
}: {
  label: string;
  question: string;
  boundary?: string;
}) => (
  <div data-testid="quick-lookup-header">
    <p className="font-head text-xs font-medium text-foreground-muted">
      Quick Lookups &middot; {label}
    </p>
    <p className="mt-1 text-sm font-medium text-foreground">{question}</p>
    {boundary && <p className="mt-1 text-xs text-foreground-muted">{boundary}</p>}
  </div>
);
